import { createHash } from "node:crypto";
import { DAY_MS } from "./window";
import type { SpecificityCode } from "./dmSignals";
import type { EntryKind } from "./qualifyLead";
import type { LeadCard } from "./leadCard";
import type { EvidenceKind } from "./types";

/**
 * U1 RESEARCH (Agent Brains 5.1 and 10, Master Spec B2). The versioned lead
 * card the research agent writes, and the pure function that builds it.
 *
 * CODES ONLY. The vault says "L3: structured lead card only (no prose)", and
 * this makes it literal. The card holds closed-catalogue codes, memory row
 * ids, evidence kinds, bucketed values and calibrated confidences. It holds no
 * customer text, no bio text, no press title and no raw follower count. The
 * owner-facing `LeadCard` (with content) stays what the web page shows. Two
 * consequences: the card cannot be quoted to a customer, because there is
 * nothing in it to quote; and the whole card is safe in a table that survives
 * erasure of the contact's text (it is deleted by cascade anyway, A6.3).
 *
 * THE MODEL NEVER GRADES ITSELF (CLAUDE.md section 4). A tone or interest
 * label comes from the model with NO confidence field; its confidence is the
 * code table `INFERENCE_PRIORS`. A fact carries a deterministic weight from its
 * evidence kind (`buildLeadCard`), never a confidence.
 *
 * NOTHING HERE READS THE CARD INSIDE A SEND GATE. Consumers check freshness
 * (`isCardFresh`) and ask for a re-research, never silently reuse a stale card
 * (Agent Brains 10).
 */

export const RESEARCH_CARD_SCHEMA = 1;

/** AWAITING RATIFICATION (Agent Brains 5.1 L5 lists refresh TTLs as T1; no learner writes it in U). */
export const LEAD_CARD_TTL_MS = 7 * DAY_MS;

export const TONE_CODES = ["tone.formal", "tone.warm", "tone.terse", "tone.unknown"] as const;
export const INTEREST_CODES = [
  "interest.engagement_ring",
  "interest.wedding_band",
  "interest.earrings",
  "interest.necklace",
  "interest.bracelet",
  "interest.watch",
  "interest.repair_item",
  "interest.other",
  "interest.unknown",
] as const;
export const VIP_SIGNAL_CODES = [
  "vip.ig_verified",
  "vip.followers_10k_100k",
  "vip.followers_100k_1m",
  "vip.followers_gte_1m",
  "vip.follows_business",
  "vip.press_mention",
  "vip.owner_history_top",
] as const;

/**
 * G57 (AB 5.1 L2 "RFM tagging on returning customers"). Recency and frequency
 * only: monetary stays out of the card (row 158, D34). Read from standing Sold
 * and Deposit outcomes (outcome.reversed folded in by the handler), each one
 * counted, a deposit folded into the sale that follows it on its thread, and
 * bucketed on the same edge numbers as the analyst's persona buckets
 * (analystAggregates.ts loadPersonaRows). Same edges, different measures: the
 * analyst's frequency bucket counts a thread's customer messages, this one
 * counts standing purchases, so the two readings of one customer can differ
 * and are never joined (TA-1296 final review, core-4).
 */
export const RFM_RECENCY_CODES = ["rfm.recency.r_30", "rfm.recency.r_90", "rfm.recency.r_old"] as const;
export const RFM_FREQUENCY_CODES = ["rfm.frequency.f_1_2", "rfm.frequency.f_3_9", "rfm.frequency.f_10_plus"] as const;
export type RfmRecencyCode = (typeof RFM_RECENCY_CODES)[number];
export type RfmFrequencyCode = (typeof RFM_FREQUENCY_CODES)[number];

// G57 (TA-1291): days since the newest standing purchase; at most `recent` is r_30, at most `mid` is r_90.
// AWAITING RATIFICATION
export const RFM_RECENCY_EDGES_DAYS: Readonly<{ recent: number; mid: number }> = Object.freeze({ recent: 30, mid: 90 });
// G57 (TA-1291): standing purchases counted; at most `low` is f_1_2, at most `mid` is f_3_9.
// AWAITING RATIFICATION
export const RFM_FREQUENCY_EDGES: Readonly<{ low: number; mid: number }> = Object.freeze({ low: 2, mid: 9 });

export interface RfmCodes {
  readonly recency: RfmRecencyCode;
  readonly frequency: RfmFrequencyCode;
}

/** The contact's standing purchases: how many standing Sold or Deposit outcomes (a deposit folded into its sale), and when the newest was recorded. */
export interface PurchaseHistory {
  readonly count: number;
  readonly lastAt: string | null;
}

/** Null for no standing purchase, or a history that cannot be read: no code rather than a guess (section 0). */
export function rfmCodesOf(p: PurchaseHistory | undefined, now: Date): RfmCodes | null {
  if (p === undefined || !Number.isInteger(p.count) || p.count < 1 || p.lastAt === null) return null;
  const last = Date.parse(p.lastAt);
  if (!Number.isFinite(last)) return null;
  const ageDays = Math.floor((now.getTime() - last) / DAY_MS);
  const recency: RfmRecencyCode =
    ageDays <= RFM_RECENCY_EDGES_DAYS.recent ? "rfm.recency.r_30" : ageDays <= RFM_RECENCY_EDGES_DAYS.mid ? "rfm.recency.r_90" : "rfm.recency.r_old";
  const frequency: RfmFrequencyCode =
    p.count <= RFM_FREQUENCY_EDGES.low ? "rfm.frequency.f_1_2" : p.count <= RFM_FREQUENCY_EDGES.mid ? "rfm.frequency.f_3_9" : "rfm.frequency.f_10_plus";
  return { recency, frequency };
}

export type ToneCode = (typeof TONE_CODES)[number];
export type InterestCode = (typeof INTEREST_CODES)[number];
export type VipSignalCode = (typeof VIP_SIGNAL_CODES)[number];
export interface ToneLabel {
  readonly tone: ToneCode;
  readonly interest: InterestCode;
}

/**
 * `memory_inference` (G41): a curated contact-memory inference carried by id
 * and evidence kind, with the confidence stored on its memory row. Its code is
 * `memory.{evidenceKind}`, which no `[LEAD]` line shape matches, so the reply
 * prompt is unchanged by it.
 */
export type InferenceMethod = "code_rule" | "model_label" | "vip_noisy_or" | "memory_inference";

/**
 * AWAITING RATIFICATION. Placeholders (0.5 for a model label, 0.7 for a code
 * rule), never graded by the model itself. INFERENCE_PRIORS is a code table,
 * not a model output (CLAUDE.md section 4). `calibrateResearchInferences`
 * (qualifyCalibration.ts, printed by `pnpm measure:qualify`, G75) reports them
 * against outcomes, record only: nothing moves a prior.
 */
export const INFERENCE_PRIORS: Readonly<Record<"model_label" | "code_rule", number>> = Object.freeze({
  model_label: 0.5,
  code_rule: 0.7,
});

/** A memory fact by reference: id, evidence kind and decayed weight, no content. */
export interface CardFactRef {
  readonly memoryId: string;
  readonly evidenceKind: EvidenceKind;
  readonly weight: number;
}
export interface CardInference {
  readonly code: string;
  readonly confidence: number;
  readonly method: InferenceMethod;
  /** Evidence kinds or signal codes. */
  readonly evidence: readonly string[];
}

export type EnrichmentSource = "none" | "mock" | "vendor";
export type EnrichmentOutcome = "disabled" | "no_subject" | "refused" | "failed" | "no_results" | "signals";
export type CardTrigger = "lead_created" | "first_turn" | "re_research";

export interface ResearchCard {
  readonly schemaVersion: 1;
  readonly contactId: string;
  readonly version: number;
  readonly builtAt: string;
  readonly ttlExpiresAt: string;
  readonly trigger: CardTrigger;
  readonly signals: {
    readonly specificity: readonly SpecificityCode[];
    readonly timing: { readonly dayPart: string; readonly weekend: boolean } | null;
    readonly entryKind: EntryKind | null;
    /**
     * G57: null for a customer with no standing purchase, and also null when
     * `purchases` was not handed in at all (`rfmCodesOf(undefined)`); the card
     * does not tell the two apart, only the inputs hash does (`purchases: null`
     * there). The research handler always hands them in (researchHandler.ts
     * `purchaseHistory`), so a stored card's null is a read with no standing
     * purchase (TA-1296 final review, core-5). Optional because a card stored
     * before G57 has no such field; `buildResearchCard` always sets it.
     */
    readonly rfm?: RfmCodes | null;
  };
  readonly facts: readonly CardFactRef[];
  readonly inferences: readonly CardInference[];
  readonly heldCount: number;
  readonly quarantinedCount: number;
  readonly excludedMalformed: number;
  readonly vip: {
    readonly flag: boolean;
    readonly confidence: number | null;
    readonly signals: readonly VipSignalCode[];
    /** True only when U2's detector computed the confidence. U1 never does. */
    readonly computed: boolean;
  };
  readonly enrichment: EnrichmentSource;
  readonly enrichmentOutcome: EnrichmentOutcome;
  readonly inputsHash: string;
}

/** What a profile lookup returned: buckets and counts only, never text (mirrors the gateway's ProfileSignals). */
export interface ProfileBuckets {
  readonly verified: boolean | null;
  readonly followerBucket: "lt_10k" | "10k_100k" | "100k_1m" | "gte_1m" | null;
  readonly followsBusiness: boolean | null;
  readonly pressMentions: number | null;
}
export interface ProfileOutcome {
  readonly outcome: EnrichmentOutcome;
  readonly source: EnrichmentSource;
  readonly signals: ProfileBuckets | null;
}

export interface BuildResearchCardInput {
  readonly contactId: string;
  readonly version: number;
  readonly trigger: CardTrigger;
  readonly lead: LeadCard;
  readonly dm: {
    readonly specificity: readonly SpecificityCode[];
    readonly timing: { readonly dayPart: string; readonly weekend: boolean } | null;
    readonly entryKind: EntryKind | null;
  };
  readonly tone: ToneLabel | null;
  readonly profile: ProfileOutcome;
  readonly ownerHistoryTop: boolean;
  /** The contact's current VIP tag and confidence. U1 reads them and computes nothing. */
  readonly vip: { readonly flag: boolean; readonly confidence: number | null };
  /** G57: the contact's standing purchases. Undefined means not read, and the card carries no RFM code. */
  readonly purchases?: PurchaseHistory;
  readonly now: Date;
}

function vipSignalsOf(profile: ProfileOutcome, ownerHistoryTop: boolean): VipSignalCode[] {
  const out: VipSignalCode[] = [];
  const s = profile.signals;
  if (s !== null) {
    if (s.verified === true) out.push("vip.ig_verified");
    if (s.followerBucket === "10k_100k") out.push("vip.followers_10k_100k");
    if (s.followerBucket === "100k_1m") out.push("vip.followers_100k_1m");
    if (s.followerBucket === "gte_1m") out.push("vip.followers_gte_1m");
    if (s.followsBusiness === true) out.push("vip.follows_business");
    // vip.press_mention is unreachable in U: a press search needs a person's
    // name, and the custody planner refuses names (Q6). pressMentions is never read.
  }
  if (ownerHistoryTop) out.push("vip.owner_history_top");
  return out;
}

function canonical(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(canonical).join(",")}]`;
  if (value !== null && typeof value === "object") {
    const entries = Object.entries(value as Record<string, unknown>)
      .filter(([, v]) => v !== undefined)
      .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
    return `{${entries.map(([k, v]) => `${JSON.stringify(k)}:${canonical(v)}`).join(",")}}`;
  }
  return JSON.stringify(value);
}

const sha = (value: unknown): string => createHash("sha256").update(canonical(value)).digest("hex");

const round3 = (n: number): number => Math.round(n * 1000) / 1000;

export function buildResearchCard(i: BuildResearchCardInput): ResearchCard {
  const facts: CardFactRef[] = i.lead.facts.map((f) => ({
    memoryId: f.memoryId,
    evidenceKind: f.evidenceKind,
    weight: round3(f.weight),
  }));

  const inferences: CardInference[] = [];
  for (const code of i.dm.specificity) {
    inferences.push({ code, confidence: INFERENCE_PRIORS.code_rule, method: "code_rule", evidence: ["dm.latest_message"] });
  }
  if (i.tone !== null) {
    for (const code of [i.tone.tone, i.tone.interest]) {
      inferences.push({ code, confidence: INFERENCE_PRIORS.model_label, method: "model_label", evidence: ["dm.latest_message"] });
    }
  }
  // G41 (MS B2: inferences are hypotheses with confidence scores): every memory
  // inference, dm.customer-claim included, by id and kind and never by content.
  // Ordered by memory id, so decay reordering the lead card never reorders this one.
  const memoryInferences = [...i.lead.inferences].sort((a, b) => (a.memoryId < b.memoryId ? -1 : a.memoryId > b.memoryId ? 1 : 0));
  for (const m of memoryInferences) {
    inferences.push({ code: `memory.${m.evidenceKind}`, confidence: m.confidence, method: "memory_inference", evidence: [m.memoryId] });
  }

  const rfm = rfmCodesOf(i.purchases, i.now);
  const vipSignals = vipSignalsOf(i.profile, i.ownerHistoryTop);
  const builtAt = i.now.toISOString();

  // The inputs hash covers what the card was derived FROM, never the decayed
  // weights or the raw clock: the same memory, message, label and profile hash
  // the same on Monday and on Friday, so a re-research with nothing new is
  // detected and writes no version. The one exception is deliberate: G57's
  // `rfm` codes are bucketed from `now`, so a re-research that crosses a
  // recency bucket edge alone versions the card.
  const inputsHash = sha({
    schema: RESEARCH_CARD_SCHEMA,
    facts: i.lead.facts.map((f) => [f.memoryId, f.evidenceKind]).sort(),
    // G41: a new or recalibrated memory inference versions the card; its decayed weight does not.
    inferences: i.lead.inferences.map((m) => [m.memoryId, m.evidenceKind, m.confidence]).sort(),
    held: i.lead.heldContradictions.map((h) => h.memoryId).sort(),
    quarantined: i.lead.quarantined.map((q) => q.memoryId).sort(),
    excludedMalformed: i.lead.excludedMalformed,
    dm: i.dm,
    tone: i.tone,
    profile: { outcome: i.profile.outcome, source: i.profile.source, signals: i.profile.signals },
    ownerHistoryTop: i.ownerHistoryTop,
    vip: i.vip,
    // G57: a new purchase versions the card, and so does a bucket crossing on re-research.
    purchases: i.purchases === undefined ? null : { count: i.purchases.count, lastAt: i.purchases.lastAt },
    rfm,
  });

  return {
    schemaVersion: 1,
    contactId: i.contactId,
    version: i.version,
    builtAt,
    ttlExpiresAt: new Date(i.now.getTime() + LEAD_CARD_TTL_MS).toISOString(),
    trigger: i.trigger,
    signals: { specificity: [...i.dm.specificity], timing: i.dm.timing, entryKind: i.dm.entryKind, rfm },
    facts,
    inferences,
    heldCount: i.lead.heldContradictions.length,
    quarantinedCount: i.lead.quarantined.length,
    excludedMalformed: i.lead.excludedMalformed,
    vip: { flag: i.vip.flag, confidence: i.vip.confidence, signals: vipSignals, computed: false },
    enrichment: i.profile.source,
    enrichmentOutcome: i.profile.outcome,
    inputsHash,
  };
}

/**
 * sha256 of the canonical card with the fields that only say WHEN or WHICH
 * stripped: version, build time, ttl and the inputs hash. Two cards that say
 * the same thing hash the same, whatever version they are.
 */
export function researchCardHash(card: ResearchCard): string {
  const { version: _v, builtAt: _b, ttlExpiresAt: _t, inputsHash: _i, ...content } = card;
  void _v;
  void _b;
  void _t;
  void _i;
  return sha(content);
}

/** True while the card's ttl is still ahead. Strict: a card expiring this instant is stale. */
export function isCardFresh(card: { readonly ttlExpiresAt: string }, now: Date): boolean {
  const t = Date.parse(card.ttlExpiresAt);
  return Number.isFinite(t) && t > now.getTime();
}
